/*
  HHA buy-path fallback (2026-08-03).

  The cart drawer app owns the only working route from "add to cart" to
  "checkout": its add feedback IS the drawer opening, and its Checkout control
  is a type="submit" button inside NO form, so it is natively inert and works
  only while the app's JS click handler is bound. When the app fails to mount
  or loses its own v1/v2 script race, an add still succeeds server-side but the
  customer sees nothing and can never reach checkout. This is the safety net.

  Three guards, all no-ops while the app is healthy:
    1. Add-watch: after a successful /cart/add, if no drawer becomes visible,
       go to /cart (whose Check Out button is a real form submit).
    2. Add click rescue: if a /cart/add form button is clicked and nothing at
       all responds, perform the add over AJAX and let the drawer open. If the
       drawer never appears, guard 1 takes over and goes to /cart. A sized
       product with no size chosen is never rescued: that add was refused on
       purpose, and rescuing it used to post a variant nobody selected.
    3. Checkout rescue: if the drawer Checkout control is clicked and the page
       neither navigates nor issues a checkout request, go to /checkout.

  Every branch prefers doing nothing over acting on a guess. No dependencies,
  no console output, one global (the re-entry guard).
*/
(function () {
  'use strict';
  try {
    if (window.__hhaBuyPath) return;
    window.__hhaBuyPath = true;
    if (window.Shopify && window.Shopify.designMode) return;
  } catch (e) { return; }

  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var CHECKOUT_RE = /\/(checkout|checkouts)(\/|\?|$)/;
  var DRAWER_SEL = '[class*="drawer-cd-up-app"], .cd-app-overlay, .opus-btn-checkout';
  var CHECKOUT_SEL = '.opus-btn-checkout, #opus-checkout-btn, .cd-checkout-section-button';
  // Only a real add control arms the rescue. A /cart/add form also contains the
  // size chips and the quantity steppers, and those are type="button" with no
  // name, so they must never be mistaken for an add that failed.
  var ATC_SEL = '.hhp-atc, [name="add"], button[type="submit"], input[type="submit"]';

  var DRAWER_WAIT = 2500; // a healthy drawer has this long to become visible
  var RESCUE_WAIT = 4000; // any add request has this long to appear after a click
  var NAV_WAIT = 2000; // a checkout click has this long to navigate on its own

  var lastAdd = 0; // last SUCCESSFUL add
  var lastAddTry = 0; // last add the server answered at all, success or not
  var lastCheckoutReq = 0;
  var watching = false;
  var leaving = false;
  var sent = false;

  function now() { return Date.now(); }

  function go(url) {
    if (leaving || sent) return;
    sent = true;
    window.location.href = url;
  }

  function onCartOrCheckout() {
    return /^\/(cart|checkout)/.test(window.location.pathname);
  }

  // beforeunload fires at the START of a navigation, so a slow but healthy
  // checkout is never preempted by the timers below. pagehide covers the rest.
  var mark = function () { leaving = true; };
  window.addEventListener('beforeunload', mark, true);
  window.addEventListener('pagehide', mark, true);

  /* --- request observation --------------------------------------------------
     The theme dispatches cart:refresh after every add it performs, which no
     fetch wrapper can take away. The taps below only widen the net to adds
     performed by apps. Both always call through first and swallow their own
     errors: observation must never change the call it is watching. */
  try {
    var origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        var out = origFetch.apply(window, arguments);
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          var method = (init && init.method) || (input && input.method) || 'GET';
          if (CHECKOUT_RE.test(url)) lastCheckoutReq = now();
          if (ADD_RE.test(url) && String(method).toUpperCase() === 'POST' && out && typeof out.then === 'function') {
            out.then(function (res) {
              // Any answer at all means the click was alive. A rejected add (sold
              // out, rate limited) is a real answer the customer was shown, so the
              // rescue must stay out of it rather than retry behind their back.
              try { lastAddTry = now(); if (res && res.ok) addSucceeded(); } catch (e) { /* noop */ }
            }, function () { try { lastAddTry = now(); } catch (e) { /* noop */ } });
          }
        } catch (e) { /* noop */ }
        return out;
      };
    }
  } catch (e) { /* leave fetch exactly as found */ }

  try {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__hhaUrl = url; this.__hhaMethod = method; } catch (e) { /* noop */ }
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        var self = this;
        this.addEventListener('load', function () {
          try {
            var url = String(self.__hhaUrl || '');
            if (CHECKOUT_RE.test(url)) lastCheckoutReq = now();
            var ok = self.status >= 200 && self.status < 300;
            var post = String(self.__hhaMethod || '').toUpperCase() === 'POST';
            if (post && ADD_RE.test(url)) { lastAddTry = now(); if (ok) addSucceeded(); }
          } catch (e) { /* noop */ }
        });
      } catch (e) { /* noop */ }
      return origSend.apply(this, arguments);
    };
  } catch (e) { /* leave XHR exactly as found */ }

  document.addEventListener('cart:refresh', function () { addSucceeded(); }, false);

  /* --- 1. add-watch -------------------------------------------------------- */
  function visible(el) {
    try {
      var r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 40) return false;
      var s = window.getComputedStyle(el);
      if (!s || s.display === 'none' || s.visibility === 'hidden') return false;
      if (parseFloat(s.opacity || '1') < 0.05) return false;
      // Require the element to be genuinely on screen, not just touching the
      // edge: the CLOSED drawer is parked just past the right edge and still
      // overlaps the viewport by a sliver, which must not read as "open".
      var vw = window.innerWidth || 0;
      var vh = window.innerHeight || 0;
      var iw = Math.min(r.right, vw) - Math.max(r.left, 0);
      var ih = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (iw <= 0 || ih <= 0) return false;
      return iw * ih >= r.width * r.height * 0.5;
    } catch (e) { return false; }
  }

  function scan(root) {
    var nodes = root.querySelectorAll(DRAWER_SEL);
    for (var i = 0; i < nodes.length; i++) if (visible(nodes[i])) return true;
    return false;
  }

  function drawerVisible() {
    try {
      if (scan(document)) return true;
      // the app can mount behind a shadow root (window.opusUseShadowRoot)
      var kids = document.body ? document.body.children : [];
      for (var i = 0; i < kids.length; i++) {
        var sr = kids[i].shadowRoot;
        if (sr && scan(sr)) return true;
      }
    } catch (e) { /* an unreadable DOM counts as "no drawer" */ }
    return false;
  }

  // Mirrors snippets/hh-cart-ui-bridge: when the app is alive but configured
  // NOT to open the drawer after an add, no drawer is the intended behaviour
  // and redirecting would be a real change. Only rescue when a drawer was owed.
  function drawerOwed() {
    try {
      var w = window.opusWidget;
      var alive = typeof window.opusOpen === 'function' || typeof window.opusRefreshCart === 'function';
      var autoOpen = !!(w && w.settings && w.settings.settings &&
        w.settings.settings.customization &&
        w.settings.settings.customization.openCartDrawerAfterAddingProduct);
      if (alive && !autoOpen) return false;
    } catch (e) { /* unknown config: assume a drawer was owed */ }
    return true;
  }

  function addSucceeded() {
    lastAdd = now();
    if (watching || leaving || sent || onCartOrCheckout()) return;
    watching = true;
    var started = now();
    var timer = setInterval(function () {
      var done = leaving || sent || drawerVisible();
      if (!done && now() - started < DRAWER_WAIT) return;
      clearInterval(timer);
      watching = false;
      if (done) return;
      if (drawerOwed()) go('/cart');
    }, 150);
  }

  /* --- 2. add-to-cart click rescue ----------------------------------------- */
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target;
      if (!t || !t.closest) return;
      var btn = t.closest('button, input[type="submit"], input[type="button"]');
      if (!btn || btn.disabled) return;
      if (!btn.matches || !btn.matches(ATC_SEL)) return; // not an add control
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      var at = now();
      setTimeout(function () {
        if (leaving || sent) return;
        if (lastAddTry >= at) return; // the add was attempted and answered
        if (btn.getAttribute('data-state')) return; // the theme's own handler ran
        if (onCartOrCheckout()) return;
        rescue(form, at);
      }, RESCUE_WAIT);
    } catch (e) { /* noop */ }
  }, true);

  // Reached only when the page's own add-to-cart JS did not run at all, so the
  // form is submitted the way a browser would with no JavaScript: a real POST
  // to /cart/add that Shopify answers with a redirect to return_to.
  function rescue(form, at) {
    // A sized product with no chip selected is a DECLINED add, not a dead click: the
    // theme refused it on purpose and is showing "Choose a size". Rescuing that is what
    // posted an unchosen size L. Silence is the only correct move here.
    try {
      var chips = form.querySelectorAll('.hhp-vsize');
      if (chips.length && !form.querySelector('.hhp-vsize.on')) return;
    } catch (e) { /* fall through to the variant check below, which is stricter now */ }
    var id = form.querySelector('[name="id"]');
    var variant = '';
    try {
      var chosen = form.querySelector('.hhp-vsize.on');
      variant = (chosen && chosen.getAttribute('data-vid')) || (id && id.value) || '';
    } catch (e) { variant = (id && id.value) || ''; }
    if (!variant) return;

    var submit = function (already) {
      if (leaving || sent) return;
      if (lastAddTry >= at) return; // an add was answered while we were checking
      // Something already put this variant in the cart, so the click was not
      // dead after all. Do nothing and let the add-watch decide about the cart.
      if (already) return;
      try {
        // WAS: a native form.submit() with return_to=/cart, which made every rescue a
        // full document POST and a page reload, landing the shopper on /cart with the
        // drawer bypassed. Now the rescue performs the SAME add the theme would have
        // done, over AJAX, and lets the drawer open.
        //
        // 'sent' is deliberately NOT set here. The fetch wrapper at the top of this file
        // sees this very request, records it and calls addSucceeded(), whose existing
        // watch gives the drawer DRAWER_WAIT to appear and falls through to /cart only if
        // it never does. Setting 'sent' would gag that fallback via go().
        if (id && id.value !== variant) id.value = variant;
        var qEl = form.querySelector('[name="quantity"]');
        var q = parseInt((qEl && qEl.value) || '1', 10);
        if (!(q > 0)) q = 1;
        window.fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(variant), quantity: q })
        })
          .then(function (r) {
            if (!r || !r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); })
          // The add itself failed, so there is nothing to show in a drawer. /cart is a
          // normal navigation, not a form POST, and its Check Out button is real.
          .catch(function () { go('/cart'); });
      } catch (e) { go('/cart'); }
    };

    // Never double-add. If this variant is already in the cart we cannot prove
    // the click did not put it there, so show the cart instead of adding again.
    try {
      window.fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          var has = false;
          var items = (cart && cart.items) || [];
          for (var i = 0; i < items.length; i++) {
            if (String(items[i].id) === String(variant)) has = true;
          }
          submit(has);
        })
        .catch(function () { submit(false); }); // cart unreadable: no add can have landed
    } catch (e) { submit(false); }
  }

  /* --- 3. drawer checkout rescue ------------------------------------------- */
  document.addEventListener('click', function (ev) {
    try {
      var path = (ev.composedPath && ev.composedPath()) || [ev.target];
      var hit = false;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el && el.matches && el.matches(CHECKOUT_SEL)) { hit = true; break; }
      }
      if (!hit) return;
      var at = now();
      var from = window.location.href;
      setTimeout(function () {
        if (leaving || sent) return;
        if (window.location.href !== from) return; // it navigated on its own
        if (lastCheckoutReq >= at) return; // checkout is already in flight
        go('/checkout');
      }, NAV_WAIT);
    } catch (e) { /* noop */ }
  }, true);
})();
